import { getSystemState } from "./SystemState";
import { pushNotification } from "./NotificationManager";

// NapflixOS BootManager v3
// Startup/health runtime state only. This does not drive visible boot UI yet.
export const BOOT_PHASES = {
  BOOT_INIT: "BOOT_INIT",
  NETWORK_WAIT: "NETWORK_WAIT",
  API_CHECK: "API_CHECK",
  PLEX_CHECK: "PLEX_CHECK",
  PJTV_CHECK: "PJTV_CHECK",
  CACHE_WARM: "CACHE_WARM",
  READY: "READY",
  DEGRADED: "DEGRADED",
};

const CHECK_TIMEOUT_MS = 5000;

let bootState = {
  phase: BOOT_PHASES.BOOT_INIT,
  networkReady:
    typeof navigator !== "undefined" ? navigator.onLine !== false : true,
  apiReady: false,
  plexReady: false,
  pjtvReady: false,
  homeCacheReady: false,
  startedAt: Date.now(),
  updatedAt: Date.now(),
  errors: [],
};

const listeners = new Set();

export function getBootState() {
  return bootState;
}

export function setBootState(partial) {
  bootState = {
    ...bootState,
    ...partial,
    updatedAt: Date.now(),
  };

  listeners.forEach((listener) => listener(bootState));
}

export function subscribeBootState(listener) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

const addBootError = (scope, error) => {
  setBootState({
    errors: [
      ...bootState.errors,
      {
        scope,
        message: error?.message || String(error),
        at: Date.now(),
      },
    ],
  });
};

const fetchWithTimeout = async (url, controllers) => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);

  controllers.add(controller);

  try {
    const response = await fetch(url, {
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`${url} returned ${response.status}`);
    }

    return response;
  } finally {
    window.clearTimeout(timeout);
    controllers.delete(controller);
  }
};

export function installBootManager() {
  if (typeof window === "undefined" || !getSystemState().isNapflixOS) {
    return () => {};
  }

  const controllers = new Set();
  let cancelled = false;

  const runBootChecks = async () => {
    setBootState({
      phase: BOOT_PHASES.BOOT_INIT,
      startedAt: Date.now(),
      errors: [],
    });

    const networkReady = navigator.onLine !== false;
    setBootState({
      phase: BOOT_PHASES.NETWORK_WAIT,
      networkReady,
    });

    if (!networkReady) {
      addBootError("network", new Error("Browser reports offline"));
    }

    setBootState({ phase: BOOT_PHASES.API_CHECK });

    try {
      await fetchWithTimeout("/api/cache/home", controllers);

      if (cancelled) return;

      setBootState({
        apiReady: true,
        homeCacheReady: true,
      });
    } catch (error) {
      if (!cancelled) addBootError("api", error);
    }

    if (cancelled) return;

    setBootState({ phase: BOOT_PHASES.PLEX_CHECK });

    try {
      await fetchWithTimeout("/api/cache/home", controllers);

      if (cancelled) return;

      setBootState({ plexReady: true });
    } catch (error) {
      if (!cancelled) addBootError("plex", error);
    }

    if (cancelled) return;

    setBootState({ phase: BOOT_PHASES.PJTV_CHECK });

    try {
      await fetchWithTimeout("/pjtv/channels", controllers);

      if (cancelled) return;

      setBootState({ pjtvReady: true });
    } catch (error) {
      if (!cancelled) addBootError("pjtv", error);
    }

    if (cancelled) return;

    setBootState({ phase: BOOT_PHASES.CACHE_WARM });

    const nextPhase =
      getBootState().errors.length > 0
        ? BOOT_PHASES.DEGRADED
        : BOOT_PHASES.READY;

    setBootState({ phase: nextPhase });

    if (nextPhase === BOOT_PHASES.READY) {
      pushNotification({
        id: "boot-ready",
        type: "boot",
        title: "Napflix ready",
        message: "Core services are online.",
        level: "success",
      });
    } else {
      pushNotification({
        id: "boot-degraded",
        type: "boot",
        title: "Napflix started with warnings",
        message: "Some startup checks did not complete.",
        level: "warning",
      });
    }
  };

  runBootChecks().catch((error) => {
    if (cancelled) return;

    addBootError("boot", error);
    setBootState({ phase: BOOT_PHASES.DEGRADED });
  });

  return () => {
    cancelled = true;
    controllers.forEach((controller) => controller.abort());
    controllers.clear();
  };
}
